import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, SharedQuestion, Variable, DataPoint } from '../api/client';
import CorrelationView from './CorrelationView';
import { formatShortDate } from '../utils/date';

const formatValue = (value: number | string | boolean | null | undefined, variable: Variable): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (variable.type === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (variable.type === 'numeric') {
    return typeof value === 'number' ? value.toFixed(2) : value.toString();
  }
  return value.toString();
};

const PublicQuestionPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [shared, setShared] = useState<SharedQuestion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const sq = await api.getSharedQuestion(slug);
        if (cancelled) return;
        setShared(sq);
        document.title = `${sq.title} — WhyNotLog`;
      } catch (err) {
        if (cancelled) return;
        setError('This page does not exist or is no longer shared.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const variables = useMemo(
    () => (shared ? Object.values(shared.dataset.variables) : []),
    [shared],
  );
  const depVariables = variables.filter(v => v.dependent);
  const indepVariables = variables.filter(v => !v.dependent);

  const sortedDataPoints = useMemo((): DataPoint[] => {
    if (!shared) return [];
    return [...shared.dataset.datapoints].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [shared]);

  if (loading) return <div className="loading">Loading...</div>;
  if (error || !shared) {
    return (
      <div className="container public-question">
        <div className="error">{error ?? 'This page is not available.'}</div>
        <p className="text-muted public-footer">
          Curious what this is? <Link to="/signup">WhyNotLog</Link> helps you
          answer everyday questions with your own data.
        </p>
      </div>
    );
  }

  const correlations = shared.dataset.correlations;
  const hasCorrelations = !!correlations && Object.values(correlations).some(
    row => Object.values(row).some(r => !r.error)
  );

  return (
    <div className="question-detail public-question container">
      <div className="question-header">
        <div className="question-title-row">
          <h2>{shared.title}</h2>
        </div>
        {shared.description && <p>{shared.description}</p>}
        <p className="text-muted public-byline">
          Shared{shared.owner_name ? ` by ${shared.owner_name}` : ''} · started{' '}
          {formatShortDate(shared.created_at)}
        </p>

        <div className="question-overview">
          <div className="overview-stats">
            <div className="overview-stat">
              <span className="stat-label">Variables</span>
              <span className="stat-value">{variables.length}</span>
              <span className="stat-sub">
                {depVariables.length} dependent, {indepVariables.length} independent
              </span>
            </div>
            <div className="overview-stat">
              <span className="stat-label">Data points</span>
              <span className="stat-value">{shared.total_datapoints}</span>
              <span className="stat-sub">
                {shared.total_datapoints === 1 ? 'entry' : 'entries'}
              </span>
            </div>
            <div className="overview-stat">
              <span className="stat-label">Correlation</span>
              <span className={`stat-value ${hasCorrelations ? 'ok' : 'pending'}`}>
                {hasCorrelations ? 'Ready' : 'Not enough data'}
              </span>
              <span className="stat-sub">
                {hasCorrelations ? 'Results available' : 'No results yet'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {variables.length > 0 && (
        <section className="public-section">
          <h3>Variables</h3>
          <ul className="variable-list">
            {variables.map(v => (
              <li key={v.id} className="variable-item">
                <div className="variable-info">
                  <strong>{v.name}</strong>
                  <span className="text-muted">
                    {' '}{v.dependent ? 'dependent' : 'independent'} · {v.type}
                  </span>
                  {v.description && <p className="text-muted">{v.description}</p>}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {depVariables.length > 0 && indepVariables.length > 0 && (
        <section className="public-section">
          <h3>Correlations</h3>
          <CorrelationView
            dependentVariables={depVariables}
            independentVariables={indepVariables}
            correlations={correlations}
          />
        </section>
      )}

      {sortedDataPoints.length > 0 && (
        <section className="public-section">
          <h3>Data points</h3>
          {shared.total_datapoints > sortedDataPoints.length && (
            <p className="text-muted">
              Showing the {sortedDataPoints.length} most recent of{' '}
              {shared.total_datapoints} data points.
            </p>
          )}
          <div className="data-points-table">
            <table>
              <thead>
                <tr>
                  <th>Recorded</th>
                  {variables.map(v => (
                    <th key={v.id}>{v.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedDataPoints.map(dp => (
                  <tr key={dp.id}>
                    <td>{formatShortDate(dp.created_at)}</td>
                    {variables.map(v => (
                      <td key={v.id}>{formatValue(dp.values?.[v.id]?.value, v)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <p className="text-muted public-footer">
        Shared via <Link to="/signup">WhyNotLog</Link> — answer your everyday
        questions with your own data.
      </p>
    </div>
  );
};

export default PublicQuestionPage;
